import {
  OPDS1,
  AppAuthMethod,
  ClientSamlMethod,
  ClientOidcMethod
} from "interfaces";

/**
 * Normalizes authentication methods from the OPDS authentication document
 * into a flat array of client-ready auth methods with unique IDs.
 *
 * Handles special cases:
 * - SAML methods with multiple IdP links are expanded into separate methods
 * - OIDC methods extract authenticate and optional logout links
 * - All methods get a unique ID (either their href or type)
 */
export function normalizeAuthMethods(
  authDoc: OPDS1.AuthDocument
): AppAuthMethod[] {
  return authDoc.authentication.reduce<AppAuthMethod[]>((flattened, method) => {
    if (isServerSamlMethod(method)) {
      return [...flattened, ...serverToClientSamlMethods(method)];
    }
    if (isServerOidcMethod(method)) {
      const oidcMethod = serverToClientOidcMethod(method);
      return oidcMethod ? [...flattened, oidcMethod] : flattened;
    }
    return [...flattened, { ...method, id: method.type }];
  }, []);
}

export const isServerSamlMethod = (
  method: OPDS1.ServerAuthMethod
): method is OPDS1.ServerSamlMethod =>
  method.type === OPDS1.SamlAuthType && "links" in method;

export const isServerOidcMethod = (
  method: OPDS1.ServerAuthMethod
): method is OPDS1.ServerOidcMethod =>
  method.type === OPDS1.OidcAuthType && "links" in method;

function serverToClientSamlMethods(
  samlMethod: OPDS1.ServerSamlMethod
): ClientSamlMethod[] {
  if (!samlMethod.links) return [];
  // The logout link is SP-level and shared across all IdPs in this method.
  const logoutLink = samlMethod.links.find(link => link.rel === "logout");
  return samlMethod.links
    .filter(link => link.rel === "authenticate")
    .map(idp => ({
      // use the href as the id for saml methods because there can be >1 saml method
      id: idp.href,
      href: idp.href,
      type: samlMethod.type,
      description:
        getEnglishValue(idp.display_names) ?? "Unknown SAML Provider",
      ...(logoutLink ? { logoutLink } : {})
    }));
}

/**
 * Converts a server-side OIDC method to a client-ready OIDC method.
 * Extracts the authenticate link and optional logout link.
 *
 * @returns ClientOidcMethod if links exist, null otherwise
 */
function serverToClientOidcMethod(
  oidcMethod: OPDS1.ServerOidcMethod
): ClientOidcMethod | null {
  if (!oidcMethod.links || oidcMethod.links.length === 0) return null;

  const authenticateLink = oidcMethod.links.find(
    link => link.rel === "authenticate"
  );
  const logoutLink = oidcMethod.links.find(link => link.rel === "logout");

  if (!authenticateLink) return null;

  // The authenticate link href serves as the unique ID.
  return {
    id: authenticateLink.href,
    href: authenticateLink.href,
    ...(logoutLink ? { logoutLink } : {}),
    type: oidcMethod.type,
    description:
      getEnglishValue(authenticateLink.display_names) ??
      oidcMethod.description ??
      "OIDC Provider"
  };
}

export const getEnglishValue = (
  arr: Array<{ language?: string; value: string }> | undefined
) => arr?.find(item => item.language === "en")?.value;

export function generateCredentials(username: string, password = "") {
  const btoaStr = btoa(`${username}:${password}`);
  return `Basic ${btoaStr}`;
}
